import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Mail, MailOpen, Eye, Paperclip, RefreshCcw, Beaker, X, Loader2 } from "lucide-react";

const KIND_LABEL = {
  proposal_send: "Envoi devis",
  proposal_send_cc: "Copie admin",
  invoice_send: "Envoi facture",
  invoice_reminder: "Relance",
  portal_welcome: "Activation portail",
  company_attached: "Entreprise rattachée",
  other: "Email",
};

const fmt = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function EmailHistoryPanel({ docType, docId, docRef }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const url = docType === "invoice" ? `/admin/invoices/${docId}/email-history` : `/admin/proposals/${docId}/email-history`;
    api.get(url)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [docType, docId]);
  useEffect(() => { load(); }, [load]);

  const openPreview = async (row) => {
    setPreview(row); setPreviewData(null);
    try { const r = await api.get(`/admin/emails/${row.id}`); setPreviewData(r.data); } catch {}
  };

  const totalSends = rows.filter((r) => !r.test_mode).length;
  const totalTests = rows.filter((r) => r.test_mode).length;
  const totalOpens = rows.reduce((acc, r) => acc + (r.open_count || 0), 0);
  const lastOpen = rows.map((r) => r.last_opened_at).filter(Boolean).sort().slice(-1)[0];

  return (
    <div className="mt-4 bg-white border border-black/8 rounded-2xl overflow-hidden" data-testid={`email-history-${docType}-${docId}`}>
      <div className="px-5 py-3 border-b border-black/8 flex items-center justify-between flex-wrap gap-2 bg-gradient-to-r from-[#06B6D4]/8 to-[#A7F3D0]/8">
        <div className="flex items-center gap-2 flex-wrap">
          <Mail size={14} className="text-[#06B6D4]" />
          <div className="display font-extrabold text-[#0A1128] text-sm">Historique d'envoi & d'ouverture</div>
          <span className="text-[10px] text-[#64748B] tabular-nums">— {docRef}</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-black/8"><Mail size={10} className="text-[#0A1128]" /> <b>{totalSends}</b> envoi{totalSends > 1 ? "s" : ""}</span>
          {totalTests > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700"><Beaker size={10} /> <b>{totalTests}</b> test{totalTests > 1 ? "s" : ""}</span>}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-black/8"><MailOpen size={10} className="text-[#065F46]" /> <b>{totalOpens}</b> ouverture{totalOpens > 1 ? "s" : ""}</span>
          {lastOpen && <span className="text-[#065F46] font-bold">Dernière ouverture : {fmt(lastOpen)}</span>}
          <button onClick={load} className="inline-flex items-center gap-1 text-[#06B6D4] hover:underline" data-testid="email-history-refresh"><RefreshCcw size={10} />Actualiser</button>
        </div>
      </div>
      {loading ? (
        <div className="p-6 text-center text-xs text-[#64748B]"><Loader2 className="inline animate-spin mr-2" size={12} />Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-[#64748B] italic">Aucun email envoyé pour ce document.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA] text-[9px] uppercase tracking-widest text-[#64748B]">
              <tr>
                <th className="text-left px-3 py-2">Date envoi</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Destinataire</th>
                <th className="text-center px-3 py-2">PJ</th>
                <th className="text-center px-3 py-2">Statut</th>
                <th className="text-left px-3 py-2">Ouverture client</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-black/5 hover:bg-[#FAFAFA]/60" data-testid={`email-history-row-${r.id}`}>
                  <td className="px-3 py-2 text-[#64748B] tabular-nums">{fmt(r.sent_at)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${r.test_mode ? "bg-purple-100 text-purple-700" : "bg-[#06B6D4]/15 text-[#06B6D4]"}`}>
                      {r.test_mode ? "TEST" : KIND_LABEL[r.kind] || r.kind}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#0A1128] font-semibold truncate max-w-[220px]">{r.to}</td>
                  <td className="px-3 py-2 text-center">{(r.attachments?.length || 0) > 0 ? <span className="inline-flex items-center gap-1 text-[#64748B]"><Paperclip size={10} />{r.attachments.length}</span> : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {r.sent
                      ? <span className="text-[9px] font-bold uppercase bg-[#A7F3D0] text-[#065F46] px-1.5 py-0.5 rounded-full">Envoyé</span>
                      : <span className="text-[9px] font-bold uppercase bg-[#FEE2E2] text-[#B91C1C] px-1.5 py-0.5 rounded-full" title={r.error || ""}>Échec</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.opened_at ? (
                      <div>
                        <div className="inline-flex items-center gap-1 text-[#065F46] font-bold"><MailOpen size={11} />Lu</div>
                        <div className="text-[10px] text-[#64748B] tabular-nums">{fmt(r.opened_at)}{r.open_count > 1 ? ` · ${r.open_count}× ouvert` : ""}</div>
                        {r.last_opened_at && r.last_opened_at !== r.opened_at && <div className="text-[9px] text-[#94a3b8]">Dernière : {fmt(r.last_opened_at)}</div>}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#64748B]"><Mail size={11} />Non ouvert</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openPreview(r)} className="text-[#06B6D4] hover:text-[#0A1128]" title="Voir l'email envoyé" data-testid={`email-history-view-${r.id}`}><Eye size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setPreview(null); setPreviewData(null); }}>
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-black/8 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-widest uppercase text-[#06B6D4]">{KIND_LABEL[preview.kind] || preview.kind}{preview.test_mode ? " · APERÇU TEST" : ""}</div>
                <div className="display font-extrabold text-[#0A1128] text-base truncate">{preview.subject}</div>
                <div className="text-xs text-[#64748B] mt-0.5">À <b>{preview.to}</b> · {fmt(preview.sent_at)}{preview.opened_at ? ` · Ouvert ${fmt(preview.opened_at)} (${preview.open_count}×)` : " · Non ouvert"}</div>
              </div>
              <button onClick={() => { setPreview(null); setPreviewData(null); }} className="text-[#64748B] hover:text-[#0A1128]"><X size={18} /></button>
            </div>
            {!previewData ? (
              <div className="p-10 text-center"><Loader2 className="inline animate-spin text-[#06B6D4]" /></div>
            ) : (
              <div className="flex-1 overflow-auto">
                <iframe srcDoc={previewData.html} title="email" className="w-full h-[62vh] bg-white" sandbox="" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
